import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TABS } from "../utils/tabs";
import { CalendarDays, Plus, UserCog, Settings, Users, NotebookPen, FileLock2 } from "lucide-react";

const GRADIENTS = [
  "from-[#26788A] to-[#134A57]",
  "from-indigo-500 to-indigo-800",
  "from-rose-500 to-rose-700",
  "from-amber-500 to-orange-700",
  "from-emerald-500 to-emerald-800",
  "from-violet-500 to-violet-800",
  "from-sky-500 to-blue-800",
  "from-fuchsia-500 to-pink-700",
  "from-cyan-500 to-teal-700",
  "from-red-500 to-rose-800",
  "from-lime-500 to-green-700",
  "from-orange-400 to-red-600",
];

const TileButton = ({ icon: Icon, label, gradientIdx, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`bg-gradient-to-br ${GRADIENTS[gradientIdx % GRADIENTS.length]} rounded-2xl p-4 h-28 flex flex-col justify-between text-white shadow-lg active:scale-95 transition-transform text-left`}
  >
    <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
      <Icon size={18} />
    </span>
    <span className="text-sm font-semibold leading-tight">{label}</span>
  </button>
);

export const MobileHome = ({ data, onNewEvent }) => {
  const navigate = useNavigate();
  const { user, canAccessTab, hasPermission } = useAuth();

  const firstName = (user?.name || "").split(" ")[0];
  const today = new Date();
  const roster = data?.roster || [];
  const present = roster.filter((r) => r.status === "work").length;
  const eventsCount = (data?.events || []).length;
  const lateCount = (data?.late_tasks || []).length;

  const tiles = TABS.filter((t) => canAccessTab(t.key));

  return (
    <div className="min-h-screen p-4 pb-24" data-testid="mobile-home">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#26788A] via-[#1D5E6C] to-[#0F2E37] p-6 text-white shadow-xl">
        <p className="text-[11px] uppercase tracking-[0.2em] font-medium text-white/60 capitalize">
          {today.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">Bonjour {firstName}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-white/80">
          <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {eventsCount} événement{eventsCount > 1 ? "s" : ""} aujourd'hui</span>
          {roster.length > 0 && <span className="flex items-center gap-1.5"><Users size={14} /> {present}/{roster.length} présent{present > 1 ? "s" : ""}</span>}
          {lateCount > 0 && <span className="text-red-200 font-medium">{lateCount} en retard</span>}
        </div>
        {onNewEvent && (
          <button
            onClick={onNewEvent}
            data-testid="mobile-new-event-btn"
            className="mt-4 flex items-center gap-2 text-sm font-medium bg-white/15 border border-white/25 px-4 py-2 rounded-full active:scale-95 transition-transform"
          >
            <Plus size={15} /> Nouvel événement
          </button>
        )}
      </div>

      {/* Accès */}
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-400 mt-6 mb-3 px-1">Mes accès</p>
      <div className="grid grid-cols-2 gap-3" data-testid="mobile-tiles">
        <TileButton icon={CalendarDays} label="Planning" gradientIdx={0} onClick={() => navigate("/mon-planning")} testId="mobile-tile-planning" />
        <TileButton icon={FileLock2} label="Bulletins de salaire" gradientIdx={5} onClick={() => navigate(user?.role === "admin" ? "/bulletins-salaire" : "/mes-bulletins")} testId="mobile-tile-payslips" />
        {!canAccessTab("tasks") && <TileButton icon={NotebookPen} label="Récap du jour" gradientIdx={4} onClick={() => navigate("/taches/recap")} testId="mobile-tile-daily-recap" />}
        {tiles.map((t, i) => (
          <TileButton key={t.key} icon={t.icon} label={t.label} gradientIdx={i + 1} onClick={() => navigate(t.to)} testId={`mobile-tile-${t.key.replace(/_/g, "-")}`} />
        ))}
        {hasPermission("manage_users") && (
          <TileButton icon={UserCog} label="Utilisateurs" gradientIdx={tiles.length} onClick={() => navigate("/utilisateurs")} testId="mobile-tile-users" />
        )}
        {hasPermission("view_settings") && (
          <TileButton icon={Settings} label="Réglages" gradientIdx={tiles.length + 1} onClick={() => navigate("/reglages")} testId="mobile-tile-settings" />
        )}
      </div>
    </div>
  );
};

export default MobileHome;
